/**
 * Workbook Name Dialog
 * 
 * A dialog styled like the AI Spotlight widget for naming the alert workbook.
 * Displayed as an overlay with blurred background when AlertWorkbook loads.
 */

import { Box, Button, Text, TextInput, Stack } from '@mantine/core';
import { Sparkle } from '@phosphor-icons/react';
import { useState, type FC, useCallback } from 'react';

// =============================================================================
// DESIGN TOKENS - Matching AI Spotlight Widget styling
// =============================================================================
const spacing = {
    xs: 8,
    sm: 16,
    md: 20,
    lg: 24,
    xl: 32,
} as const;

// Spotlight container - subtle glow effect (from AISpotlightWidget)
const spotlightStyles = {
    boxShadow: `
        0 1px 3px rgba(0, 0, 0, 0.04),
        0 4px 12px rgba(0, 0, 0, 0.03),
        0 0 0 1px rgba(59, 130, 246, 0.12),
        0 0 12px rgba(59, 130, 246, 0.06),
        0 8px 32px rgba(0, 0, 0, 0.08)
    `,
    background: 'linear-gradient(135deg, #FFFFFF 0%, rgba(248, 250, 255, 1) 100%)',
    borderRadius: 16,
} as const;

interface WorkbookNameDialogProps {
    onConfirm: (alertLabel: string) => void;
    onCancel: () => void;
}

const WorkbookNameDialog: FC<WorkbookNameDialogProps> = ({ onConfirm, onCancel }) => {
    const [workbookName, setWorkbookName] = useState<string>('');

    const handleConfirm = useCallback(() => {
        const trimmedName = workbookName.trim();
        if (trimmedName) {
            onConfirm(trimmedName);
        }
    }, [workbookName, onConfirm]);

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && workbookName.trim()) {
            handleConfirm();
        }
    };

    const isValid = workbookName.trim().length > 0;

    return (
        /* Overlay - blurred backdrop */
        <Box
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                zIndex: 1000,
            }}
        >
            {/* Dialog Container - AI Spotlight styled */}
            <Box
                style={{
                    ...spotlightStyles,
                    padding: spacing.xl,
                    minWidth: 400,
                    maxWidth: 480,
                }}
            >
                <Stack gap={spacing.md}>
                    {/* Header with Sparkle icon */}
                    <Box style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                        <Sparkle 
                            size={20} 
                            weight="fill" 
                            style={{ 
                                color: 'rgb(59, 130, 246)',
                                filter: 'drop-shadow(0 0 3px rgba(59, 130, 246, 0.4))',
                            }}
                        />
                        <Text 
                            style={{ 
                                fontSize: 20,
                                fontWeight: 600,
                                lineHeight: 1.3,
                                color: 'rgb(26, 32, 44)',
                            }}
                        >
                            Spinning up an alert agent for you...
                        </Text>
                    </Box>

                    {/* Subtext */}
                    <Text 
                        style={{ 
                            fontSize: 14,
                            lineHeight: 1.5,
                            color: 'rgb(100, 116, 139)',
                        }}
                    >
                        Give it a name first
                    </Text>

                    {/* Text Input */}
                    <TextInput
                        placeholder="An identifier for your alert rule"
                        value={workbookName}
                        onChange={(e) => setWorkbookName(e.target.value)}
                        onKeyPress={handleKeyPress}
                        radius="md"
                        size="md"
                        autoFocus
                        styles={{
                            input: {
                                fontSize: '15px',
                                border: '1px solid rgba(59, 130, 246, 0.2)',
                                transition: 'border-color 150ms ease, box-shadow 150ms ease',
                                '&:focus': {
                                    borderColor: 'rgb(59, 130, 246)',
                                    boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.1)',
                                },
                            },
                        }}
                    />

                    {/* Actions */}
                    <Box style={{ display: 'flex', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.xs }}>
                        <Button
                            variant="subtle"
                            color="gray"
                            onClick={onCancel}
                            radius="md"
                            size="sm"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirm}
                            disabled={!isValid}
                            radius="md"
                            size="sm"
                            variant="filled"
                            color="dark"
                        >
                            Continue
                        </Button>
                    </Box>
                </Stack>
            </Box>
        </Box>
    );
};

export default WorkbookNameDialog;
